import React, { useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "../i18n/I18nContext";
import { listUnrenewedStudents, listActiveStudents, createRegistration, listFrequencies, printComprovativo } from "../services/student";
import { getSucursalInfo, getUserInfo, isGlobalAcademy, hasSpecialHourSchedule } from "../services/auth";
import { formatCurrencyMZN } from "../utils/paymentHelpers";
import { getSucursalVocab } from "../utils/classesLabels";
import { listPaymentTypes } from "../services/paymentTypes";
import StepCard from "./shared/StepCard";

const currentYear = new Date().getFullYear();
// Same range used for the "Ano Lectivo" pickers across the app (ListaEstudantesLayer et al).
const YEAR_OPTIONS = Array.from({ length: currentYear + 1 - 2024 + 1 }, (_, i) => 2024 + i);
const FILTER_YEAR_STORAGE_KEY = "STUDENT_RENEW_FILTER_YEAR";

// Jul-Dec: a estas alturas a maioria das renovações já são para o ano
// lectivo seguinte (que arranca em Jan/Fev); Jan-Jun: a maioria ainda é
// para regularizar o ano lectivo em curso. Usado só como valor por omissão
// — o operador pode sempre escolher outro ano.
const CURRENT_MONTH = new Date().getMonth() + 1; // 1-12
const DEFAULT_RENEWAL_YEAR = CURRENT_MONTH >= 7 ? currentYear + 1 : currentYear;

// "Ano Lectivo Anterior" (filtro de pendentes): Jul-Dec já estamos a
// renovar sobretudo o ano em curso (que só agora está a terminar), por
// isso o "anterior" relevante é o ano actual; Jan-Jun ainda estamos a
// regularizar o ano lectivo passado, por isso o "anterior" é currentYear-1.
const DEFAULT_FILTER_YEAR = CURRENT_MONTH >= 7 ? currentYear : currentYear - 1;

// Este ecrã (listagem + modal de renovação) nunca usa a palavra "Curso" —
// mesmo numa sucursal com hasCorse=1, mostramos sempre "Classe", que é a
// terminologia correcta no contexto de transição de ano/classe lectiva.
const renewalLevelKey = (key, sucursalOverride) => {
  const vocab = getSucursalVocab(sucursalOverride);
  return `${key}.${vocab === "curso" ? "classe" : vocab}`;
};

// Classe seguinte na lista `list` (ordenada pela API por createdAt
// ascendente) a partir de `currentId`. Sem próxima classe (último
// nível/formatura) ou classe actual não encontrada na lista, mantém a
// classe actual — o operador pode sempre ajustar manualmente.
const findNextFrequencyId = (list, currentId) => {
  if (!Array.isArray(list) || list.length === 0) return currentId || "";
  if (!currentId) return list[0]?.id || "";
  const idx = list.findIndex((f) => String(f.id) === String(currentId));
  if (idx === -1 || idx === list.length - 1) return currentId;
  return list[idx + 1]?.id || currentId;
};

const KINSHIP_LABEL_KEYS = {
  PAI: "estudante.kinship.father",
  MAE: "estudante.kinship.mother",
  TIO: "estudante.kinship.uncle",
  AVO: "estudante.kinship.grandparent",
  IRMAO: "estudante.kinship.sibling",
};

// 0..35% in 5% steps (mirrors the discount ladder sge_infantil_web's renewal
// screen offers — src/pages/student/renew/index.js#handleSelectDiscount),
// plus 75% and 100% for full/near-full bolsas — same two extra tiers already
// available in CadastrarEstudanteLayer/EditarEstudanteLayer's DISCOUNT_OPTIONS.
const DISCOUNT_STEPS = [0, 0.05, 0.1, 0.15, 0.2, 0.25, 0.3, 0.35, 0.75, 1];

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" });
};

const initialsOf = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "?";

// Where the discount applies — mirrors the backend's applyToRegistration /
// applyToPayment flags (routes/registration.js POST /registration), which
// are independent switches over the same `discount` fraction.
const DISCOUNT_TARGETS = ["registration", "monthly", "both"];

const emptyForm = {
  year: DEFAULT_RENEWAL_YEAR,
  corseId: "",
  discount: 0,
  discountTarget: "registration",
  kinshipDegree: "",
  carierName: "",
  jobLocation: "",
  carierContact: "",
  address: "",
  scheduleType: "7h-12h",
  paymentMethod: "",
  registrationAmountPaid: 0,
};

const RenovacaoInscricaoLayer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [registrations, setRegistrations] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [filterYear, setFilterYear] = useState(() => {
    const saved = parseInt(localStorage.getItem(FILTER_YEAR_STORAGE_KEY), 10);
    return Number.isFinite(saved) ? saved : DEFAULT_FILTER_YEAR;
  });
  const [search, setSearch] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [renewedCount, setRenewedCount] = useState(0);

  const sucursal = getSucursalInfo();
  // Sucursais o utilizador tem acesso — permite escolher, dentro do modal de
  // renovação, uma sucursal diferente da actual para efectivamente transferir
  // o estudante (ex.: creche -> escola) ao renovar.
  const userSucursals = getUserInfo()?.sucursals || [];
  const canPickSucursal = userSucursals.length > 1;

  const [target, setTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // 'form' = a editar os dados da renovação; 'confirm' = ecrã de revisão
  // final antes de criar de facto a nova inscrição.
  const [step, setStep] = useState("form");
  // Sucursal seleccionada para a nova matrícula — por omissão a sucursal
  // actual do estudante. Alterá-la noutra sucursal do utilizador transfere o
  // estudante para lá, no ano lectivo seleccionado.
  const [selectedSucursal, setSelectedSucursal] = useState(sucursal);
  // Frequências/Classes da sucursal seleccionada no modal — separado do
  // `frequencies` usado na tabela/filtro principal (sempre da sucursal
  // actual), para não afectar a listagem quando o operador troca a
  // sucursal dentro do modal de renovação.
  const [modalFrequencies, setModalFrequencies] = useState([]);
  const [loadingModalFrequencies, setLoadingModalFrequencies] = useState(false);
  // `submitting` state only blocks the button on the *next* render — a fast
  // double-click/double-tap fires both handlers before React re-renders and
  // could otherwise slip a second createRegistration through. This ref is
  // checked and set synchronously, closing that gap.
  const submittingRef = useRef(false);

  // Other active registrations the target student already has for form.year
  // — used to block creating a second registration for the same student in
  // the same year (or, at Global Academy, the same student+course+year).
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  const fetchStudents = (year) => {
    setLoading(true);
    listUnrenewedStudents(year)
      .then((resp) => {
        // The backend endpoint behind listUnrenewedStudents matches a
        // registration when EITHER its own `year` OR its `lastRegistrationYear`
        // equals the filter (routes/registration.js GET
        // /current/registration/:sucursalId), then keeps only the most
        // recent row per student. Our own renewal payload sets
        // `lastRegistrationYear` to the *previous* year on the brand-new
        // record it creates — so right after a student is renewed, their
        // new current-year registration also matches the old filterYear
        // query (via lastRegistrationYear) and, being the newest row, wins
        // the "most recent per student" pick. Net effect: a just-renewed
        // student would still show up here as "pending" if we trusted the
        // backend's filter at face value. Re-filter to the row's own
        // `year` so only genuinely-unrenewed registrations remain.
        const rows = (resp.data || []).filter((r) => String(r.year) === String(year));
        setRegistrations(rows);
      })
      .catch(() => {
        setRegistrations([]);
        toast.error(t("renovacao.error.load"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStudents(filterYear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterYear]);

  useEffect(() => {
    listFrequencies()
      .then((resp) => setFrequencies(resp.data || []))
      .catch(() => setFrequencies([]));
    listPaymentTypes()
      .then((resp) => setPaymentMethods(resp.data || []))
      .catch(() => setPaymentMethods([]));
  }, []);

  const frequencyFor = (record) => {
    const student = record?.student || record;
    return frequencies.find(
      (f) => String(f.id) === String(record?.corseId) || String(f.level) === String(student?.level),
    );
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return registrations.filter((r) => {
      const student = r.student || r;
      const matchesTerm =
        !term ||
        student.name?.toLowerCase().includes(term) ||
        student.code?.toLowerCase().includes(term);
      const matchesLevel =
        !levelFilter ||
        String(r.corseId) === String(levelFilter) ||
        String(student.level) === String(levelFilter);
      return matchesTerm && matchesLevel;
    });
  }, [registrations, search, levelFilter]);

  const handleFilterYearChange = (year) => {
    localStorage.setItem(FILTER_YEAR_STORAGE_KEY, String(year));
    setFilterYear(year);
    setExpandedId(null);
  };

  const clearFilters = () => {
    setSearch("");
    setLevelFilter("");
  };

  const toggleExpand = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const openRenew = (record) => {
    const student = record.student || record;
    const carier = student.carier || {};
    const currentCorseId = record.corseId || student.level || "";
    setTarget(record);
    setForm({
      ...emptyForm,
      // Não muda de sucursal (caso mais comum): avança para a classe
      // seguinte à que o estudante já tinha, não a mesma classe.
      corseId: findNextFrequencyId(frequencies, currentCorseId),
      kinshipDegree: carier.kinshipDegree || "",
      carierName: carier.name || "",
      jobLocation: carier.workPlace || "",
      carierContact: carier.contact || student.mobile || "",
      address: carier.address || student.address || "",
      scheduleType: record.scheduleType || student.scheduleType || "7h-12h",
    });
    setErrors({});
    setStep("form");
    // Reabre sempre a partir da sucursal actual do estudante — a escolha de
    // uma sucursal diferente é feita de novo em cada renovação.
    setSelectedSucursal(sucursal);
    setModalFrequencies(frequencies);
  };

  const handleSucursalChange = (sucursalId) => {
    const next = userSucursals.find((s) => String(s.id) === String(sucursalId)) || sucursal;
    setSelectedSucursal(next);

    const isHomeSucursal = String(next.id) === String(sucursal.id);
    if (isHomeSucursal) {
      setModalFrequencies(frequencies);
      const student = target?.student || target || {};
      const currentCorseId = target?.corseId || student.level || "";
      setForm((f) => ({ ...f, corseId: findNextFrequencyId(frequencies, currentCorseId) }));
      return;
    }

    setLoadingModalFrequencies(true);
    listFrequencies(next.id)
      .then((resp) => {
        const list = resp.data || [];
        setModalFrequencies(list);
        // Transferência (ex.: creche -> escola): pré-seleccionar a primeira
        // classe/nível da sucursal de destino.
        setForm((f) => ({ ...f, corseId: list[0]?.id || "" }));
      })
      .catch(() => setModalFrequencies([]))
      .finally(() => setLoadingModalFrequencies(false));
  };

  const closeRenew = () => {
    if (submitting) return;
    setTarget(null);
  };

  const handleKinshipChange = (value) => {
    const student = target?.student || target || {};
    setForm((f) => {
      if (value === "PAI" && student.fatherName) {
        return { ...f, kinshipDegree: value, carierName: student.fatherName, carierContact: student.fatherContact || f.carierContact };
      }
      if (value === "MAE" && student.motherName) {
        return { ...f, kinshipDegree: value, carierName: student.motherName, carierContact: student.motherContact || f.carierContact };
      }
      // Renewal already has guardian data loaded most of the time — unlike the
      // brand-new-student form, we don't wipe carierName/carierContact when
      // switching to Tio/Avô/Irmão, only when there's nothing useful to fill in.
      return { ...f, kinshipDegree: value };
    });
  };

  const studentTarget = target?.student || target;
  const selectedFrequency = modalFrequencies.find((f) => String(f.id) === String(form.corseId));
  const baseRegistrationValue = parseFloat(
    selectedFrequency?.recurigRegistrationValue ?? selectedFrequency?.registrationValue ?? 0,
  ) || 0;
  // Mirrors the backend's rule (routes/registration.js, routes/student.js
  // PUT /edit/:id): '7h-15h' uses the course's special-hour fee when set,
  // otherwise falls back to the standard fee.
  const baseMonthlyPayment =
    form.scheduleType === "7h-15h" && parseFloat(selectedFrequency?.specialHourMonthlyValue ?? 0) > 0
      ? parseFloat(selectedFrequency?.specialHourMonthlyValue)
      : parseFloat(selectedFrequency?.monthlyPayment ?? 0) || 0;
  const applyDiscountToRegistration = form.discount > 0 && (form.discountTarget === "registration" || form.discountTarget === "both");
  const applyDiscountToMonthly = form.discount > 0 && (form.discountTarget === "monthly" || form.discountTarget === "both");
  const registrationDiscountAmount = applyDiscountToRegistration ? baseRegistrationValue * form.discount : 0;
  const total = Math.max(0, baseRegistrationValue - registrationDiscountAmount);
  const monthlyDiscountAmount = applyDiscountToMonthly ? baseMonthlyPayment * form.discount : 0;
  const discountedMonthlyPayment = Math.max(0, baseMonthlyPayment - monthlyDiscountAmount);

  // O valor pago da Renovação nasce igual ao valor (já descontado) calculado acima — pagar
  // tudo continua a ser o comportamento por omissão — e é reposto sempre que `total` muda
  // (classe/desconto), para nunca deixar um "Valor Pago" obsoleto de uma renovação anterior.
  useEffect(() => {
    setForm((f) => ({ ...f, registrationAmountPaid: total }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [total]);

  const registrationTaxRemaining = Math.max(0, parseFloat(total || 0) - parseFloat(form.registrationAmountPaid || 0));
  const isRegistrationTaxPartial = registrationTaxRemaining > 0.01;

  // Secção "Pagamento da Renovação" — método de pagamento + valor pago (total ou parcial) da
  // taxa de Renovação. Mostrada tanto no passo "form" como no passo "confirm", tal como
  // financialSummary.
  const registrationPaymentSection = (
    <StepCard
      icon='solar:wallet-money-outline'
      title={t("renovacao.payment.title")}
      subtitle={t("renovacao.payment.subtitle")}
      accent='success'
    >
      <div className='row gy-3'>
        <div className='col-sm-6'>
          <label className='form-label'>{t("estudante.field.paymentMethod")} <span className='required-asterisk'>*</span></label>
          <select
            className={`form-control form-select ${errors.paymentMethod ? "border border-danger-600" : ""}`}
            value={form.paymentMethod}
            onChange={(e) => setForm((f) => ({ ...f, paymentMethod: e.target.value }))}
          >
            <option value=''>{t("estudante.select.placeholder")}</option>
            {paymentMethods.map((m) => (
              <option key={m.id} value={m.name}>
                {m.name}
              </option>
            ))}
          </select>
          {errors.paymentMethod && (
            <span className='text-danger-600 text-xs'>{errors.paymentMethod}</span>
          )}
        </div>
        <div className='col-sm-6'>
          <label className='form-label'>{t("estudante.registrationPayment.amountPaid")} <span className='required-asterisk'>*</span></label>
          <input
            type='number'
            className={`form-control ${errors.registrationAmountPaid ? "border border-danger-600" : ""}`}
            min={0}
            max={total}
            value={form.registrationAmountPaid}
            onChange={(e) => {
              const raw = parseFloat(e.target.value);
              const max = parseFloat(total) || 0;
              const clamped = Number.isNaN(raw) ? 0 : Math.max(0, Math.min(raw, max));
              setForm((f) => ({ ...f, registrationAmountPaid: clamped }));
            }}
          />
          {errors.registrationAmountPaid && (
            <span className='text-danger-600 text-xs'>{errors.registrationAmountPaid}</span>
          )}
        </div>
        <div className='col-12'>
          <div
            className={`p-12 radius-8 text-center ${isRegistrationTaxPartial ? "bg-danger-50" : "bg-success-50"}`}
          >
            <span
              className={`text-sm d-block ${isRegistrationTaxPartial ? "text-danger-600" : "text-success-600"}`}
            >
              {t("propina.field.amountToPay")}
            </span>
            <strong className={isRegistrationTaxPartial ? "text-danger-600" : "text-success-600"}>
              {formatCurrencyMZN(registrationTaxRemaining)}
            </strong>
          </div>
        </div>
      </div>
    </StepCard>
  );

  // Partilhado entre o passo de edição e o de confirmação — mesmos valores,
  // para não haver risco de divergência entre o que se edita e o que se
  // confirma.
  const financialSummary = (
    <div className='p-16 radius-8 border'>
      <div className='d-flex justify-content-between text-sm mb-8'>
        <span className='text-secondary-light'>{t("renovacao.summary.baseValue")}</span>
        <span>{formatCurrencyMZN(baseRegistrationValue)}</span>
      </div>
      {applyDiscountToRegistration && (
        <div className='d-flex justify-content-between text-sm mb-8 text-danger-600'>
          <span>
            {t("renovacao.summary.discountApplied")} ({Math.round(form.discount * 100)}%)
          </span>
          <span>- {formatCurrencyMZN(registrationDiscountAmount)}</span>
        </div>
      )}
      <hr className='my-8' />
      <div className='d-flex justify-content-between align-items-center mb-8'>
        <span className='fw-semibold'>{t("renovacao.summary.total")}</span>
        <span className='text-primary-600 fw-bold text-xl'>{formatCurrencyMZN(total)}</span>
      </div>
      <hr className='my-8' />
      <div className='d-flex justify-content-between text-sm text-secondary-light mb-4'>
        <span>{t("renovacao.field.monthlyPayment")}</span>
        <span className={applyDiscountToMonthly ? "text-decoration-line-through" : ""}>
          {formatCurrencyMZN(baseMonthlyPayment)}
        </span>
      </div>
      {applyDiscountToMonthly && (
        <div className='d-flex justify-content-between text-sm fw-semibold'>
          <span>
            {t("renovacao.summary.monthlyAfterDiscount")} ({Math.round(form.discount * 100)}%)
          </span>
          <span>{formatCurrencyMZN(discountedMonthlyPayment)}</span>
        </div>
      )}
    </div>
  );

  const globalAcademy = isGlobalAcademy(selectedSucursal);

  // Re-check whenever the target student, the chosen year or the chosen
  // sucursal changes — the operator can switch the "Ano Lectivo" select
  // or pick a different sucursal (transfer) before confirming, and a check
  // against the wrong year/sucursal would be worse than no check at all.
  useEffect(() => {
    const studentId = studentTarget?.id;
    if (!studentId) {
      setDuplicateMatches([]);
      return undefined;
    }
    let cancelled = false;
    setCheckingDuplicate(true);
    listActiveStudents(form.year, selectedSucursal?.id)
      .then((resp) => {
        if (cancelled) return;
        // Match strictly on the row's own `year`, not on excluding
        // `target.id` — listUnrenewedStudents can (see fetchStudents above)
        // surface a student's own brand-new current-year registration as if
        // it were still "pending" for the old filterYear, in which case
        // `target` IS that already-created registration and an
        // exclude-by-id check would blind us to the exact duplicate we're
        // about to create again.
        const matches = (resp.data || []).filter(
          (r) => String(r.studentId || r.student?.id) === String(studentId) && String(r.year) === String(form.year),
        );
        setDuplicateMatches(matches);
      })
      .catch(() => {
        if (!cancelled) setDuplicateMatches([]);
      })
      .finally(() => {
        if (!cancelled) setCheckingDuplicate(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentTarget?.id, form.year, selectedSucursal?.id]);

  const sameCourseDuplicate = duplicateMatches.some((m) => String(m.corseId) === String(form.corseId));
  const otherCourseDuplicate = duplicateMatches.some((m) => String(m.corseId) !== String(form.corseId));
  // Global Academy is the only sucursal allowed to hold two active
  // registrations for the same student+year — but never for the same
  // course twice. Everywhere else, any existing registration for that
  // student+year blocks the renewal outright.
  const isDuplicateBlocked = globalAcademy ? sameCourseDuplicate : duplicateMatches.length > 0;

  const validate = () => {
    const next = {};
    if (!form.corseId) next.corseId = t(renewalLevelKey("renovacao.error.requiredFields", selectedSucursal));
    if (!form.kinshipDegree) next.kinshipDegree = t("renovacao.error.requiredGuardian");
    if (!form.carierName.trim()) next.carierName = t("renovacao.error.requiredGuardian");
    if (!form.carierContact || form.carierContact.replace(/\D/g, "").length !== 9) {
      next.carierContact = t("renovacao.error.invalidContact");
    }
    if (!form.paymentMethod) next.paymentMethod = t("validation.required");
    const amountPaid = parseFloat(form.registrationAmountPaid);
    if (!(amountPaid > 0) || amountPaid > parseFloat(total) + 0.01) {
      next.registrationAmountPaid = t("estudante.validation.registrationAmountPaid");
    }
    setErrors(next);
    return next;
  };

  // Passo 1 -> 2: valida os dados do formulário e, se estiverem bem, avança
  // para o ecrã de confirmação — a criação da inscrição só acontece lá,
  // em handleSubmit.
  const handleReview = () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      // Mostra a mensagem do primeiro campo em falta/inválido (não sempre a de
      // "seleccione a classe/nível/curso" — eram sempre essa, qualquer que fosse
      // o campo que falhasse, o que confundia quando era outro campo, ex.: método
      // de pagamento ou valor pago da Renovação).
      toast.error(Object.values(validationErrors)[0]);
      return;
    }
    if (checkingDuplicate || isDuplicateBlocked) {
      toast.error(t("renovacao.error.duplicateYear", { year: form.year }));
      return;
    }
    setStep("confirm");
  };

  const handleSubmit = async () => {
    if (!target || submittingRef.current) return;
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      toast.error(Object.values(validationErrors)[0]);
      setStep("form");
      return;
    }
    if (checkingDuplicate || isDuplicateBlocked) {
      toast.error(t("renovacao.error.duplicateYear", { year: form.year }));
      return;
    }

    submittingRef.current = true;
    // `target.student` can carry a stray `registrationData` blob (old
    // registration data persisted onto the STUDENT_TABLE row itself by a
    // previous flow, not something this component builds) — it's dead
    // weight for a renewal, since new payments are generated server-side,
    // and for students with a long payment history it's large enough to
    // push the request past the WAF's body-size limit (403 Forbidden on
    // POST /api/registration/registration). Drop it before sending.
    const { registrationData, ...student } = target.student || target;
    const userId = getUserInfo()?.id;

    // Same body-size trap as registrationData above, found in `target.extraconfigs`: the
    // backend (composeExtraConfigPayments, utils/RegistrationHelper.js) always OVERWRITES
    // each extraconfig's `.payments` with a freshly generated array — the incoming one is
    // never read. But `target.extraconfigs[*].payments` is the OLD year's full monthly
    // payment history per extra-curricular activity, and each of those payment entries
    // embeds yet another full copy of `student` — for a student with a few activities and a
    // few years of history this alone can reach tens of KB. Send only what the backend
    // actually uses ({id, name, recurringAmount}), mirroring what CadastrarEstudanteLayer
    // already sends for a brand-new registration.
    const cleanExtraconfigs = (target.extraconfigs || student.extraconfigs || []).map((ea) => ({
      id: ea.id,
      name: ea.name,
      recurringAmount: ea.recurringAmount,
    }));

    const carier = {
      name: form.carierName.trim(),
      kinshipDegree: form.kinshipDegree,
      contact: form.carierContact.trim(),
      workPlace: form.jobLocation.trim(),
      address: form.address.trim(),
      createdBy: userId,
      activatedBy: userId,
    };

    setSubmitting(true);
    try {
      const resp = await createRegistration({
        monthlyPayment: baseMonthlyPayment,
        totalPaid: total,
        registrationValue: total,
        total,
        discount: form.discount,
        // The backend only actually deducts `discount` from a given amount
        // when its matching apply* flag is set (routes/registration.js POST
        // /registration) — sge_infantil_web's renewal screen picks a discount
        // but never sends either flag, so the value it shows the operator
        // never reaches what's actually charged. applyToRegistration/
        // applyToPayment are independent switches over the same `discount`
        // fraction, which is exactly what the "Renovação / Propinas / Ambos"
        // picker controls.
        applyToRegistration: applyDiscountToRegistration,
        applyToPayment: applyDiscountToMonthly,
        isDiscountNumeric: false,
        student: { ...student, carier },
        studentId: student.id,
        isNew: false,
        year: form.year,
        sucursal: selectedSucursal,
        classId: form.corseId,
        corseId: form.corseId,
        level: form.corseId,
        scheduleType: form.scheduleType,
        degree: target.degree || "",
        createdBy: userId,
        activatedBy: userId,
        extraconfigs: cleanExtraconfigs,
        lastRegistrationYear: target.year || filterYear,
        hasRegistrationRegularized: true,
        unregularized: false,
        ivaPercentage: target.ivaPercentage || 0,
        mobile: carier.contact,
        extraCurricularTotal: target.extraCurricularTotal || 0,
        includeDiscount: form.discount > 0,
        registrationTaxValue: total,
        registrationTaxAmountPaid: form.registrationAmountPaid,
        registrationTaxPaymentMethod: form.paymentMethod,
      });

      toast.success(t("renovacao.success.renewed"));
      setRenewedCount((c) => c + 1);
      closeRenew();
      fetchStudents(filterYear);

      try {
        const pdfResp = await printComprovativo({
          registrationId: resp.data?.id,
          studentId: student.id,
          isRenewal: true,
          sucursalId: selectedSucursal?.id,
        });
        if (pdfResp?.data?.pdf) {
          navigate("/documentos/pdf", {
            state: {
              url: pdfResp.data.pdf,
              title: t("renovacao.documento.title", { name: student.name }),
              filename: `renovacao-${student.name}.pdf`,
            },
          });
        }
      } catch (e) {
        // Comprovativo printing is best-effort — the renewal itself already succeeded.
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || t("renovacao.error.renew"));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <div className='row gy-4'>
      <ToastContainer />

      <div className='col-12'>
        <div className='d-flex flex-wrap align-items-center gap-3'>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-warning-focus text-warning-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:account-sync-outline' />
              </span>
              <div>
                <h6 className='mb-0'>{filtered.length}</h6>
                <span className='text-secondary-light text-sm'>{t("renovacao.stat.pending")}</span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-success-focus text-success-main d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='mdi:check-decagram-outline' />
              </span>
              <div>
                <h6 className='mb-0'>{renewedCount}</h6>
                <span className='text-secondary-light text-sm'>{t("renovacao.stat.renewedSession")}</span>
              </div>
            </div>
          </div>
          <div className='card flex-fill radius-12 border-0 shadow-sm'>
            <div className='card-body d-flex align-items-center gap-3 py-16 px-20'>
              <span className='w-44-px h-44-px radius-12 bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center text-xl'>
                <Icon icon='solar:calendar-outline' />
              </span>
              <div>
                <h6 className='mb-0'>{filterYear}</h6>
                <span className='text-secondary-light text-sm'>{t("renovacao.field.filterYear")}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className='col-12'>
        <div className='card radius-12'>
          <div className='card-body'>
            <div className='mb-24'>
              <h6 className='mb-4'>{t("renovacao.breadcrumb")}</h6>
              <p className='text-secondary-light mb-0 text-sm'>{t("renovacao.subtitle")}</p>
            </div>

            <div className='p-16 radius-8 bg-neutral-50 mb-24'>
              <div className='row gy-3 align-items-end'>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t("renovacao.field.filterYear")}</label>
                  <select
                    className='form-control form-select'
                    value={filterYear}
                    onChange={(e) => handleFilterYearChange(Number(e.target.value))}
                  >
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-4'>
                  <label className='form-label text-sm mb-8'>{t("global.name")}</label>
                  <div className='icon-field'>
                    <span className='icon top-50 translate-middle-y'>
                      <Icon icon='ion:search-outline' />
                    </span>
                    <input
                      type='text'
                      className='form-control'
                      placeholder={t("renovacao.field.search.placeholder")}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className='col-sm-6 col-lg-3'>
                  <label className='form-label text-sm mb-8'>{t(renewalLevelKey("renovacao.col.previousLevel"))}</label>
                  <select
                    className='form-control form-select'
                    value={levelFilter}
                    onChange={(e) => setLevelFilter(e.target.value)}
                  >
                    <option value=''>{t(renewalLevelKey("renovacao.filter.level.placeholder"))}</option>
                    {frequencies.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div className='col-sm-6 col-lg-2'>
                  <button
                    type='button'
                    className='btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2'
                    onClick={clearFilters}
                  >
                    <Icon icon='ic:round-refresh' />
                    {t("global.clean.fields")}
                  </button>
                </div>
              </div>
            </div>

            <div className='table-responsive scroll-sm'>
              <table className='table bordered-table mb-0'>
                <thead>
                  <tr>
                    <th scope='col' style={{ width: 36 }} />
                    <th scope='col'>{t("global.name")}</th>
                    <th scope='col'>{t("estudante.field.code")}</th>
                    <th scope='col'>{t(renewalLevelKey("renovacao.col.previousLevel"))}</th>
                    <th scope='col'>{t("global.contact")}</th>
                    <th scope='col'>{t("renovacao.col.lastRegistration")}</th>
                    <th scope='col'>{t("renovacao.col.year")}</th>
                    <th scope='col' className='text-end'>
                      {t("global.col.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={8} className='text-center text-secondary-light py-32'>
                        {t("estudante.list.loading")}
                      </td>
                    </tr>
                  )}
                  {!loading && registrations.length === 0 && (
                    <tr>
                      <td colSpan={8} className='text-center text-secondary-light py-32'>
                        {t("renovacao.hint.noStudents")}
                      </td>
                    </tr>
                  )}
                  {!loading && registrations.length > 0 && filtered.length === 0 && (
                    <tr>
                      <td colSpan={8} className='text-center text-secondary-light py-32'>
                        {t("renovacao.empty.filtered")}
                      </td>
                    </tr>
                  )}
                  {filtered.map((record) => {
                    const student = record.student || record;
                    const freq = frequencyFor(record);
                    const isExpanded = expandedId === record.id;
                    return (
                      <React.Fragment key={record.id}>
                        <tr role='button' onClick={() => toggleExpand(record.id)} className={isExpanded ? "bg-neutral-50" : ""}>
                          <td>
                            <Icon
                              icon={isExpanded ? "ic:round-expand-less" : "ic:round-expand-more"}
                              className='text-secondary-light text-xl'
                            />
                          </td>
                          <td className='fw-semibold text-primary-light'>{student.name}</td>
                          <td>{student.code || "-"}</td>
                          <td>
                            {freq ? (
                              <span className='px-12 py-4 radius-8 fw-medium text-sm bg-primary-50 text-primary-600'>
                                {freq.description}
                              </span>
                            ) : (
                              "-"
                            )}
                          </td>
                          <td>{student.carier?.contact || student.mobile || "-"}</td>
                          <td>{formatDate(record.createdAt)}</td>
                          <td>{record.year || filterYear}</td>
                          <td className='text-end' onClick={(e) => e.stopPropagation()}>
                            <button
                              type='button'
                              className='btn btn-primary-600 btn-sm d-flex align-items-center gap-1 ms-auto'
                              onClick={() => openRenew(record)}
                            >
                              <Icon icon='mdi:refresh' />
                              {t("renovacao.action.renew")}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={8} className='p-0'>
                              <div className='p-24 bg-neutral-25 border-top'>
                                <div className='row gy-3'>
                                  <div className='col-md-2 text-center'>
                                    <div className='w-80-px h-80-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center mx-auto text-xl fw-bold'>
                                      {initialsOf(student.name)}
                                    </div>
                                  </div>
                                  <div className='col-md-10'>
                                    <div className='row gy-2 text-sm'>
                                      <div className='col-sm-4'>
                                        <span className='text-secondary-light d-block'>{t("global.birth.date")}</span>
                                        <strong>{formatDate(student.birthDate)}</strong>
                                      </div>
                                      <div className='col-sm-4'>
                                        <span className='text-secondary-light d-block'>{t("estudante.field.contact")}</span>
                                        <strong>{student.carier?.contact || student.mobile || "-"}</strong>
                                      </div>
                                      <div className='col-sm-4'>
                                        <span className='text-secondary-light d-block'>{t("renovacao.detail.lastMonthly")}</span>
                                        <strong>{formatCurrencyMZN(record.monthlyPayment || record.currentMonthlyPayment || 0)}</strong>
                                      </div>
                                      <div className='col-sm-4'>
                                        <span className='text-secondary-light d-block'>{t("estudante.field.carierName")}</span>
                                        <strong>{student.carier?.name || "-"}</strong>
                                      </div>
                                      <div className='col-sm-4'>
                                        <span className='text-secondary-light d-block'>{t("estudante.field.kinshipDegree")}</span>
                                        <strong>{student.carier?.kinshipDegree || "-"}</strong>
                                      </div>
                                      <div className='col-sm-4'>
                                        <span className='text-secondary-light d-block'>{t("estudante.field.studentAddress")}</span>
                                        <strong>{student.address || "-"}</strong>
                                      </div>
                                    </div>
                                    <div className='mt-16'>
                                      <button
                                        type='button'
                                        className='btn btn-primary-600 btn-sm d-flex align-items-center gap-1'
                                        onClick={() => openRenew(record)}
                                      >
                                        <Icon icon='mdi:refresh' />
                                        {t("renovacao.action.renew")}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {target && (
        <div className='modal show d-block' style={{ background: "rgba(0,0,0,0.55)" }} tabIndex={-1}>
          <div className='modal-dialog modal-dialog-centered modal-lg'>
            <div className='modal-content radius-12 border-0 overflow-hidden'>
              <div className='modal-header border-0 bg-primary-50 py-20 px-24'>
                <div className='d-flex align-items-center gap-3'>
                  <span className='w-48-px h-48-px rounded-circle bg-primary-600 text-white d-flex align-items-center justify-content-center text-lg fw-bold flex-shrink-0'>
                    {initialsOf(studentTarget?.name)}
                  </span>
                  <div>
                    <h6 className='mb-0'>{t("renovacao.modal.title")}</h6>
                    <span className='text-secondary-light text-sm'>{studentTarget?.name}</span>
                  </div>
                </div>
                <button type='button' className='btn-close' onClick={closeRenew} disabled={submitting} />
              </div>

              <div className='modal-body p-24' style={{ maxHeight: "70vh", overflowY: "auto" }}>
                <p className='text-secondary-light text-sm mb-20'>
                  {step === "confirm" ? t("renovacao.confirm.title") : t("renovacao.modal.subtitle")}
                </p>

                <div className='d-flex flex-wrap gap-4 mb-24 p-16 radius-8 bg-neutral-50'>
                  <div>
                    <span className='text-xs text-secondary-light d-block'>{t(renewalLevelKey("renovacao.col.previousLevel"))}</span>
                    <strong>{frequencyFor(target)?.description || "-"}</strong>
                  </div>
                  <div>
                    <span className='text-xs text-secondary-light d-block'>{t("renovacao.col.lastRegistration")}</span>
                    <strong>{formatDate(target.createdAt)}</strong>
                  </div>
                  <div>
                    <span className='text-xs text-secondary-light d-block'>{t("renovacao.detail.lastMonthly")}</span>
                    <strong>{formatCurrencyMZN(target.monthlyPayment || target.currentMonthlyPayment || 0)}</strong>
                  </div>
                </div>

                {checkingDuplicate && (
                  <div className='d-flex align-items-center gap-2 text-secondary-light text-sm mb-20'>
                    <Icon icon='line-md:loading-twotone-loop' />
                    {t("renovacao.duplicate.checking")}
                  </div>
                )}

                {!checkingDuplicate && isDuplicateBlocked && (
                  <div className='alert alert-danger d-flex align-items-start gap-2 mb-20'>
                    <Icon icon='mingcute:alert-fill' className='text-lg mt-2 flex-shrink-0' />
                    <div>
                      <strong className='d-block'>{t("renovacao.error.duplicateYear.title")}</strong>
                      {/* Always phrased around the student+year, never the selected
                          class — the class picked in the form isn't what makes this
                          a duplicate, the existing registration for that year is. */}
                      <span className='text-sm'>{t("renovacao.error.duplicateYear", { year: form.year })}</span>
                    </div>
                  </div>
                )}

                {!checkingDuplicate && !isDuplicateBlocked && globalAcademy && otherCourseDuplicate && (
                  <div className='alert alert-info d-flex align-items-start gap-2 mb-20'>
                    <Icon icon='mingcute:information-fill' className='text-lg mt-2 flex-shrink-0' />
                    <div>
                      <strong className='d-block'>{t("renovacao.info.duplicateAllowed.title")}</strong>
                      <span className='text-sm'>{t("renovacao.info.duplicateAllowed")}</span>
                    </div>
                  </div>
                )}

                {String(selectedSucursal?.id) !== String(sucursal?.id) && (
                  <div className='alert alert-warning d-flex align-items-start gap-2 mb-20'>
                    <Icon icon='mdi:bus-school' className='text-lg mt-2 flex-shrink-0' />
                    <div>
                      <strong className='d-block'>{t("renovacao.info.transfer.title")}</strong>
                      <span className='text-sm'>
                        {t("renovacao.info.transfer", { sucursal: selectedSucursal?.name || selectedSucursal?.description || "", year: form.year })}
                      </span>
                    </div>
                  </div>
                )}

                {step === "form" && (
                <div className='row gy-4'>
                  <div className='col-lg-7'>
                    <StepCard
                      icon='solar:square-academic-cap-bold'
                      title={t("renovacao.section.newRegistration")}
                      accent='primary'
                    >
                    <div className='row gy-3'>
                      <div className='col-sm-6'>
                        <label className='form-label text-sm mb-8'>{t("renovacao.field.sucursal")}</label>
                        <select
                          className='form-control form-select'
                          value={selectedSucursal?.id || ""}
                          onChange={(e) => handleSucursalChange(e.target.value)}
                          disabled={!canPickSucursal}
                        >
                          {(canPickSucursal ? userSucursals : [sucursal]).map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name || s.description}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label text-sm mb-8'>{t("turma.field.academicYear")}</label>
                        <select
                          className='form-control form-select'
                          value={form.year}
                          onChange={(e) => setForm((f) => ({ ...f, year: Number(e.target.value) }))}
                        >
                          {YEAR_OPTIONS.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label text-sm mb-8'>
                          {t(renewalLevelKey("turma.field.educationLevel", selectedSucursal))} *
                        </label>
                        <select
                          className={`form-control form-select ${errors.corseId ? "border border-danger-600" : ""}`}
                          value={form.corseId}
                          onChange={(e) => setForm((f) => ({ ...f, corseId: e.target.value }))}
                          disabled={loadingModalFrequencies}
                        >
                          <option value=''>{t(renewalLevelKey("turma.field.educationLevel.placeholder", selectedSucursal))}</option>
                          {modalFrequencies.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.description}
                            </option>
                          ))}
                        </select>
                        {errors.corseId && <span className='text-danger-600 text-xs'>{errors.corseId}</span>}
                      </div>
                      {hasSpecialHourSchedule(selectedSucursal) && (
                        <div className='col-sm-6'>
                          <label className='form-label text-sm mb-8'>{t("estudante.field.scheduleType")}</label>
                          <select
                            className='form-control form-select'
                            value={form.scheduleType}
                            onChange={(e) => setForm((f) => ({ ...f, scheduleType: e.target.value }))}
                          >
                            <option value='7h-12h'>{t("estudante.field.scheduleType.standard")}</option>
                            <option value='7h-15h'>{t("estudante.field.scheduleType.special")}</option>
                          </select>
                        </div>
                      )}
                      <div className='col-12'>
                        <label className='form-label text-sm mb-8'>{t("renovacao.field.discount")}</label>
                        <div className='d-flex flex-wrap gap-2'>
                          {DISCOUNT_STEPS.map((d) => (
                            <button
                              key={d}
                              type='button'
                              className={`btn btn-sm radius-8 ${form.discount === d ? "btn-primary-600" : "btn-outline-secondary"}`}
                              onClick={() => setForm((f) => ({ ...f, discount: d }))}
                            >
                              {Math.round(d * 100)}%
                            </button>
                          ))}
                        </div>
                      </div>
                      {form.discount > 0 && (
                        <div className='col-12'>
                          <label className='form-label text-sm mb-8'>{t("renovacao.field.discountTarget")}</label>
                          <div className='d-flex flex-wrap gap-2'>
                            {DISCOUNT_TARGETS.map((opt) => (
                              <button
                                key={opt}
                                type='button'
                                className={`btn btn-sm radius-8 ${form.discountTarget === opt ? "btn-primary-600" : "btn-outline-secondary"}`}
                                onClick={() => setForm((f) => ({ ...f, discountTarget: opt }))}
                              >
                                {t(`renovacao.field.discountTarget.${opt}`)}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    </StepCard>

                    {registrationPaymentSection}
                    <div className='mt-20'>{financialSummary}</div>
                  </div>

                  <div className='col-lg-5'>
                    <StepCard
                      icon='solar:user-id-bold'
                      title={t("renovacao.section.guardian")}
                      accent='info'
                    >
                    <div className='row gy-3'>
                      <div className='col-12'>
                        <label className='form-label text-sm mb-8'>{t("estudante.field.kinshipDegree")} <span className='required-asterisk'>*</span></label>
                        <select
                          className={`form-control form-select ${errors.kinshipDegree ? "border border-danger-600" : ""}`}
                          value={form.kinshipDegree}
                          onChange={(e) => handleKinshipChange(e.target.value)}
                        >
                          <option value=''>{t("estudante.select.placeholder")}</option>
                          <option value='PAI'>{t("estudante.kinship.father")}</option>
                          <option value='MAE'>{t("estudante.kinship.mother")}</option>
                          <option value='TIO'>{t("estudante.kinship.uncle")}</option>
                          <option value='AVO'>{t("estudante.kinship.grandparent")}</option>
                          <option value='IRMAO'>{t("estudante.kinship.sibling")}</option>
                        </select>
                        {errors.kinshipDegree && <span className='text-danger-600 text-xs'>{errors.kinshipDegree}</span>}
                      </div>
                      <div className='col-12'>
                        <label className='form-label text-sm mb-8'>{t("estudante.field.carierName")} <span className='required-asterisk'>*</span></label>
                        <input
                          type='text'
                          className={`form-control ${errors.carierName ? "border border-danger-600" : ""}`}
                          value={form.carierName}
                          onChange={(e) => setForm((f) => ({ ...f, carierName: e.target.value }))}
                        />
                        {errors.carierName && <span className='text-danger-600 text-xs'>{errors.carierName}</span>}
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label text-sm mb-8'>{t("estudante.field.carierContact")} <span className='required-asterisk'>*</span></label>
                        <input
                          type='text'
                          className={`form-control ${errors.carierContact ? "border border-danger-600" : ""}`}
                          value={form.carierContact}
                          onChange={(e) => setForm((f) => ({ ...f, carierContact: e.target.value }))}
                        />
                        {errors.carierContact && <span className='text-danger-600 text-xs'>{errors.carierContact}</span>}
                      </div>
                      <div className='col-sm-6'>
                        <label className='form-label text-sm mb-8'>{t("estudante.field.jobLocation")}</label>
                        <input
                          type='text'
                          className='form-control'
                          value={form.jobLocation}
                          onChange={(e) => setForm((f) => ({ ...f, jobLocation: e.target.value }))}
                        />
                      </div>
                      <div className='col-12'>
                        <label className='form-label text-sm mb-8'>{t("estudante.field.address")}</label>
                        <textarea
                          className='form-control'
                          rows={3}
                          value={form.address}
                          onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                        />
                      </div>
                    </div>
                    </StepCard>
                  </div>
                </div>
                )}

                {step === "confirm" && (
                  <div className='row gy-4'>
                    <div className='col-12'>
                      <div className='alert alert-primary d-flex align-items-start gap-2 mb-0'>
                        <Icon icon='mdi:information-outline' className='text-lg mt-2 flex-shrink-0' />
                        <span className='text-sm'>{t("renovacao.confirm.hint")}</span>
                      </div>
                    </div>

                    <div className='col-lg-7'>
                      <StepCard
                        icon='solar:square-academic-cap-bold'
                        title={t("renovacao.section.newRegistration")}
                        accent='primary'
                      >
                      <div className='row gy-3 text-sm'>
                        <div className='col-sm-6'>
                          <span className='text-secondary-light d-block'>{t("renovacao.field.sucursal")}</span>
                          <strong>{selectedSucursal?.name || selectedSucursal?.description || "-"}</strong>
                        </div>
                        <div className='col-sm-6'>
                          <span className='text-secondary-light d-block'>{t("turma.field.academicYear")}</span>
                          <strong>{form.year}</strong>
                        </div>
                        <div className='col-sm-6'>
                          <span className='text-secondary-light d-block'>
                            {t(renewalLevelKey("turma.field.educationLevel", selectedSucursal))}
                          </span>
                          <strong>{selectedFrequency?.description || "-"}</strong>
                        </div>
                        {hasSpecialHourSchedule(selectedSucursal) && (
                          <div className='col-sm-6'>
                            <span className='text-secondary-light d-block'>{t("estudante.field.scheduleType")}</span>
                            <strong>
                              {form.scheduleType === "7h-15h"
                                ? t("estudante.field.scheduleType.special")
                                : t("estudante.field.scheduleType.standard")}
                            </strong>
                          </div>
                        )}
                        <div className='col-sm-6'>
                          <span className='text-secondary-light d-block'>{t("renovacao.field.discount")}</span>
                          <strong>
                            {Math.round(form.discount * 100)}%
                            {form.discount > 0 ? ` · ${t(`renovacao.field.discountTarget.${form.discountTarget}`)}` : ""}
                          </strong>
                        </div>
                      </div>
                      </StepCard>

                      {registrationPaymentSection}
                      <div className='mt-20'>{financialSummary}</div>
                    </div>

                    <div className='col-lg-5'>
                      <StepCard
                        icon='solar:user-id-bold'
                        title={t("renovacao.section.guardian")}
                        accent='info'
                      >
                      <div className='row gy-3 text-sm'>
                        <div className='col-12'>
                          <span className='text-secondary-light d-block'>{t("estudante.field.kinshipDegree")}</span>
                          <strong>{t(KINSHIP_LABEL_KEYS[form.kinshipDegree] || "estudante.select.placeholder")}</strong>
                        </div>
                        <div className='col-12'>
                          <span className='text-secondary-light d-block'>{t("estudante.field.carierName")}</span>
                          <strong>{form.carierName || "-"}</strong>
                        </div>
                        <div className='col-sm-6'>
                          <span className='text-secondary-light d-block'>{t("estudante.field.carierContact")}</span>
                          <strong>{form.carierContact || "-"}</strong>
                        </div>
                        <div className='col-sm-6'>
                          <span className='text-secondary-light d-block'>{t("estudante.field.jobLocation")}</span>
                          <strong>{form.jobLocation || "-"}</strong>
                        </div>
                        <div className='col-12'>
                          <span className='text-secondary-light d-block'>{t("estudante.field.address")}</span>
                          <strong>{form.address || "-"}</strong>
                        </div>
                      </div>
                      </StepCard>
                    </div>
                  </div>
                )}
              </div>

              <div className='modal-footer border-0 px-24 pb-24'>
                {step === "form" && (
                  <>
                    <button type='button' className='btn btn-outline-secondary' onClick={closeRenew} disabled={submitting}>
                      {t("global.cancel")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 d-flex align-items-center gap-2'
                      onClick={handleReview}
                      disabled={submitting || checkingDuplicate || isDuplicateBlocked}
                    >
                      <Icon icon='mdi:arrow-right-circle-outline' />
                      {t("renovacao.action.review")}
                    </button>
                  </>
                )}
                {step === "confirm" && (
                  <>
                    <button
                      type='button'
                      className='btn btn-outline-secondary d-flex align-items-center gap-2'
                      onClick={() => setStep("form")}
                      disabled={submitting}
                    >
                      <Icon icon='mdi:arrow-left' />
                      {t("renovacao.action.back")}
                    </button>
                    <button
                      type='button'
                      className='btn btn-primary-600 d-flex align-items-center gap-2'
                      onClick={handleSubmit}
                      disabled={submitting || checkingDuplicate || isDuplicateBlocked}
                    >
                      <Icon icon={submitting ? "line-md:loading-twotone-loop" : "mdi:check-circle-outline"} />
                      {submitting ? t("global.loading") : t("renovacao.action.confirm")}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RenovacaoInscricaoLayer;
